// File: src/pages/medisense/MediSenseAbout.jsx
import React, { useState } from "react";
import { Link } from "react-router-dom";

const groupNames = [
  { label: "Talking Label", emoji: "🩺🔊" },
  { label: "MeetTheDoctor", emoji: "👨‍⚕️💬" },
  { label: "MediFinders", emoji: "🔍💊" },
  { label: "Lifescience LnD", emoji: "🧬📚" },
  { label: "MediSense Productivity", emoji: "⚙️📈" },
  { label: "Clinical Governance", emoji: "⚖️🩺" },
  { label: "Clinical Operations", emoji: "🏥📋" },
  { label: "Health Data Exchange", emoji: "🔐📡" },
  { label: "MediSense CCM", emoji: "🤝📅" },
  { label: "MRWorld", emoji: "🧑‍💼📒" },
  { label: "Self Reflection", emoji: "🪞🧠" },
  { label: "Digital Income", emoji: "💰🌱" },
];

const features = [
  { title: "Universal Accessibility", emoji: "🌎", desc: "QR-based labels with zoomable fonts and voice guidance in multiple languages, ensuring anyone can access vital medical info." },
  { title: "Patient Safety", emoji: "🛡️", desc: "Instantly readable expiry, composition, and dosage information reduces medical errors and enhances safety." },
  { title: "Digital Integration", emoji: "📲", desc: "Seamlessly integrates with apps, EMRs, and hospital systems for real-time patient data." },
  { title: "Gamified Engagement", emoji: "🎯", desc: "Users earn badges, points, and rewards for using the platform, improving awareness and compliance." },
  { title: "AI Assistance", emoji: "🤖", desc: "AI-driven insights for personalized health tips, reminders, and medication adherence tracking." },
  { title: "Global Standards", emoji: "🏥", desc: "Designed to meet international medical labeling standards and accessibility norms." },
  { title: "Analytics & Insights", emoji: "📊", desc: "Aggregated anonymized data helps hospitals, pharma, and regulators improve patient care and supply chain efficiency." },
];

const tabs = [
  { id: "vision", label: "👁️ Vision & Mission" },
  { id: "features", label: "✨ Why MediSense" },
  { id: "groups", label: "🗂️ Platform Groups" },
  { id: "menu", label: "📋 Menu / Use Cases" },
  { id: "roles", label: "👥 Roles" },
  { id: "faq", label: "❓ FAQs" },
];

const menuButtons1 = [
  { label: "Talking Label", emoji: "🩺🔊", link: "/tala", group: "Talking Label" },
  { label: "MeetTheDoctor", emoji: "👨‍⚕️💬", link: "/meetthedoctor", group: "MeetTheDoctor" },
  { label: "MedicineFinder", emoji: "🔍💊", link: "/medicinefinder", group: "MediFinders" },
  { label: "beSure Medicines", emoji: "🛡️💊", link: "/besure", group: "MediFinders" },
  { label: "beSure Devices", emoji: "🛡️🩻", link: "/besure", group: "MediFinders" },
  { label: "beSure Consumables", emoji: "🛡️🧪", link: "/besure", group: "MediFinders" },
  { label: "Consultants", emoji: "🧑‍⚕️🧠", link: "/think5whys-counsellors", group: "MediFinders" },
  { label: "Second Opinion", emoji: "🩺🔄", link: "/secondopinion", group: "MediFinders" },
  { label: "New Medicines", emoji: "🧬✨", link: "/newmedicines", group: "MediFinders" },
];

export default function MediSenseAbout() {
  const [activeTab, setActiveTab] = useState("vision");

  return (
    <div className="min-h-screen flex flex-col bg-black text-white [&_p]:text-white [&_h3]:text-yellow-200 [&_h4]:text-green-200 [&_ul]:text-white [&_li]:text-white">
      <header className="flex flex-col justify-start items-start border-b border-green-500 px-4 py-3 sticky top-0 bg-black z-50 shadow-md">
        <h1 className="text-xl lg:text-2xl font-extrabold text-green-400">MediSense</h1>
        <p className="text-sm lg:text-base text-white">Accessible, Safe & Smart Medical Labeling Ecosystem</p>
      </header>

      <nav className="bg-black border-b border-green-500 sticky top-[64px] z-40 p-2">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 place-items-center">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-center text-xs lg:text-sm font-semibold px-2 py-2 rounded-md transition ${
                activeTab === tab.id
                  ? "bg-green-700 text-yellow-200 border-b-2 border-green-400"
                  : "bg-green-900 text-white hover:bg-green-700 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      <main className="flex-1 p-4 space-y-6">
        {activeTab === "vision" && (
          <section className="space-y-3">
            <h2 className="text-xl font-bold text-center text-green-400">Vision & Mission</h2>
            <div className="bg-black p-3 rounded-xl shadow-md">
              <h3 className="font-semibold mb-2">🌟 Vision</h3>
              <p>
                To make medical information universally accessible and safe through QR-enabled labels, empowering patients and healthcare providers globally.
              </p>
            </div>
            <div className="bg-black p-3 rounded-xl shadow-md">
              <h3 className="font-semibold mb-2">🎯 Mission</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Provide QR-based labels with zoom and voice guidance.</li>
                <li>Ensure clarity on expiry, composition, dosage, and safety information.</li>
                <li>Integrate with digital health systems for real-time access.</li>
                <li>Gamify engagement to improve awareness and adherence.</li>
                <li>Leverage AI for personalized health guidance and analytics.</li>
              </ul>
            </div>
          </section>
        )}

        {activeTab === "features" && (
          <section>
            <h2 className="text-xl font-bold text-center text-green-400 mb-4">Why Choose MediSense?</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {features.map((f, i) => (
                <div key={i} className="bg-black p-5 rounded-xl shadow-md text-center">
                  <div className="text-2xl mb-2">{f.emoji}</div>
                  <h4 className="font-semibold mb-1">{f.title}</h4>
                  <p className="text-sm text-gray-300">{f.desc}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {activeTab === "groups" && (
          <section>
            <h2 className="text-xl font-bold text-center text-green-400 mb-4">Platform Groups</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {groupNames.map((group, i) => (
                <div key={i} className="bg-black p-4 rounded-xl shadow-md text-center">
                  <div className="text-3xl mb-2">{group.emoji}</div>
                  <h4 className="font-semibold">{group.label}</h4>
                </div>
              ))}
            </div>
          </section>
        )}

        {activeTab === "menu" && (
          <section>
            <h2 className="text-xl font-bold text-center text-green-400 mb-4">Menu / Use Cases</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              {menuButtons1.map((item, i) => (
                <Link
                  key={i}
                  to={item.link}
                  className="bg-black p-3 rounded-xl shadow-md flex items-center gap-3 hover:bg-green-700 transition"
                >
                  <span className="text-2xl">{item.emoji}</span>
                  <span className="font-semibold">{item.label}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* ===================== ROLES ===================== */}
        {activeTab === "roles" && (
          <section className="space-y-4">
            <h2 className="text-xl font-bold text-center text-green-400">
              👥 MediSense – Roles & Stakeholders
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Patients & Caregivers */}
              <div className="bg-black p-4 rounded-xl shadow-md">
                <h3 className="font-semibold text-green-300 mb-2">🧑‍🦽 Patients & Caregivers</h3>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Scan QR to read expiry, dosage, composition clearly</li>
                  <li>Listen to medicine instructions in local language</li>
                  <li>Track adherence, reminders, and outcomes</li>
                  <li>Reduce medication errors and misuse</li>
                </ul>
              </div>

              {/* Doctors & Clinicians */}
              <div className="bg-black p-4 rounded-xl shadow-md">
                <h3 className="font-semibold text-green-300 mb-2">👨‍⚕️ Doctors & Clinicians</h3>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Prescribe with confidence using verified medicine data</li>
                  <li>Access patient adherence and feedback (opt-in)</li>
                  <li>Second opinions and clinical collaboration</li>
                  <li>Reduced OPD explanation burden</li>
                </ul>
              </div>

              {/* Pharmacists */}
              <div className="bg-black p-4 rounded-xl shadow-md">
                <h3 className="font-semibold text-green-300 mb-2">💊 Pharmacists</h3>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Ensure correct medicine dispensing</li>
                  <li>Explain medicines via QR instead of verbal repetition</li>
                  <li>Counterfeit & expiry risk reduction</li>
                  <li>Digital trust with consumers</li>
                </ul>
              </div>

              {/* Pharma & Life Sciences */}
              <div className="bg-black p-4 rounded-xl shadow-md">
                <h3 className="font-semibold text-green-300 mb-2">🏭 Pharma & Life Sciences</h3>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Regulatory-compliant digital labels</li>
                  <li>Real-world usage insights (anonymized)</li>
                  <li>Reduced recall & compliance risk</li>
                  <li>Patient education at scale</li>
                </ul>
              </div>

              {/* Regulators & Institutions */}
              <div className="bg-black p-4 rounded-xl shadow-md">
                <h3 className="font-semibold text-green-300 mb-2">🏛️ Regulators & Institutions</h3>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Standardized, auditable digital labeling</li>
                  <li>Improved patient safety compliance</li>
                  <li>Faster dissemination of safety updates</li>
                  <li>Public health awareness enablement</li>
                </ul>
              </div>

              {/* Medical Representatives */}
              <div className="bg-black p-4 rounded-xl shadow-md">
                <h3 className="font-semibold text-green-300 mb-2">🧑‍💼 Medical Representatives</h3>
                <ul className="list-disc list-inside text-sm space-y-1">
                  <li>Unified product knowledge hub</li>
                  <li>Ethical, data-driven engagement</li>
                  <li>Meeting analytics & learning loops</li>
                  <li>Higher credibility with doctors</li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {/* ===================== ROLE-BASED FAQ ===================== */}
        {activeTab === "faq" && (
          <section className="bg-black space-y-6 [&_p]:text-white">
            <h2 className="text-xl font-bold text-center text-green-400">
              ❓ MediSense – Role-Based FAQs
            </h2>

            {/* ===================== PATIENTS & CAREGIVERS ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">
                🧑‍🦽 Patients & Caregivers
              </h3>

              <div className="text-white">
                <p className="font-semibold text-red-400">😟 I am Pessimist</p>
                <p className="text-sm text-gray-300">
                  What if I misunderstand the digital information or depend too much on it?
                </p>
                <p className="text-sm mt-1">
                  MediSense does not replace doctors or pharmacists. It only clarifies
                  what is already legally printed on the label—making it readable,
                  audible, and understandable.
                </p>
              </div>

              <div>
                <p className="font-semibold text-yellow-400">🙂 I am OK</p>
                <p className="text-sm text-gray-300">
                  How does this help me in daily medicine use?
                </p>
                <p className="text-sm mt-1">
                  You can instantly check expiry, dosage, timing, precautions, and
                  hear instructions in your language—especially helpful for elders
                  and low-vision users.
                </p>
              </div>

              <div>
                <p className="font-semibold text-green-400">🚀 I will Use it</p>
                <p className="text-sm text-gray-300">
                  Can this make me more confident and independent?
                </p>
                <p className="text-sm mt-1">
                  Yes. MediSense empowers you to manage medicines safely, track
                  adherence, and participate actively in your own healthcare.
                </p>
              </div>
            </div>

            {/* ===================== DOCTORS & CLINICIANS ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">
                👨‍⚕️ Doctors & Clinicians
              </h3>

              <div>
                <p className="font-semibold text-red-400">😟 Pessimist</p>
                <p className="text-sm text-gray-300">
                  Will this increase patient confusion or misinterpretation?
                </p>
                <p className="text-sm mt-1">
                  No. MediSense standardizes and simplifies explanations you already
                  give—reducing repetitive clarification and improving compliance.
                </p>
              </div>

              <div>
                <p className="font-semibold text-yellow-400">🙂 Normal</p>
                <p className="text-sm text-gray-300">
                  How does this fit into my clinical workflow?
                </p>
                <p className="text-sm mt-1">
                  Patients come better informed. QR-based explanations reduce OPD time,
                  improve adherence, and support follow-ups.
                </p>
              </div>

              <div>
                <p className="font-semibold text-green-400">🚀 Optimist</p>
                <p className="text-sm text-gray-300">
                  Can this improve outcomes and trust?
                </p>
                <p className="text-sm mt-1">
                  Yes. Better-informed patients follow treatment more accurately,
                  leading to improved outcomes and higher doctor credibility.
                </p>
              </div>
            </div>

            {/* ===================== PHARMACISTS ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">
                💊 Pharmacists
              </h3>

              <div>
                <p className="font-semibold text-red-400">😟 Pessimist</p>
                <p className="text-sm text-gray-300">
                  Will this replace my role or reduce interaction?
                </p>
                <p className="text-sm mt-1">
                  No. MediSense supports you by reinforcing your instructions digitally,
                  not replacing your professional judgment.
                </p>
              </div>

              <div>
                <p className="font-semibold text-yellow-400">🙂 Normal</p>
                <p className="text-sm text-gray-300">
                  Does this help reduce errors?
                </p>
                <p className="text-sm mt-1">
                  Yes. Clear expiry, batch, and dosage visibility reduces dispensing
                  errors and improves customer confidence.
                </p>
              </div>

              <div>
                <p className="font-semibold text-green-400">🚀 Optimist</p>
                <p className="text-sm text-gray-300">
                  Can this strengthen trust with customers?
                </p>
                <p className="text-sm mt-1">
                  Absolutely. Digital transparency positions you as a modern,
                  safety-first healthcare professional.
                </p>
              </div>
            </div>

            {/* ===================== PHARMA & LIFE SCIENCES ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">
                🏭 Pharma & Life Sciences
              </h3>

              <div>
                <p className="font-semibold text-red-400">😟 Pessimist</p>
                <p className="text-sm text-gray-300">
                  Does this increase regulatory or legal risk?
                </p>
                <p className="text-sm mt-1">
                  No. MediSense extends approved label content digitally and helps
                  demonstrate proactive patient safety compliance.
                </p>
              </div>

              <div>
                <p className="font-semibold text-yellow-400">🙂 Normal</p>
                <p className="text-sm text-gray-300">
                  What business value does this bring?
                </p>
                <p className="text-sm mt-1">
                  Reduced misuse, fewer recalls, better brand trust, and anonymized
                  real-world insights.
                </p>
              </div>

              <div>
                <p className="font-semibold text-green-400">🚀 Optimist</p>
                <p className="text-sm text-gray-300">
                  Can this redefine patient engagement?
                </p>
                <p className="text-sm mt-1">
                  Yes. MediSense transforms medicines from static products into
                  interactive, intelligent healthcare experiences.
                </p>
              </div>
            </div>

            {/* ===================== REGULATORS ===================== */}
            <div className="bg-black p-4 rounded-xl shadow-md space-y-3">
              <h3 className="font-semibold text-green-300">
                🏛️ Regulators & Public Institutions
              </h3>

              <div>
                <p className="font-semibold text-red-400">😟 Pessimist</p>
                <p className="text-sm text-gray-300">
                  Does this complicate enforcement and monitoring?
                </p>
                <p className="text-sm mt-1">
                  On the contrary, it creates auditable, updateable, and standardized
                  digital extensions of physical labels.
                </p>
              </div>

              <div>
                <p className="font-semibold text-yellow-400">🙂 Normal</p>
                <p className="text-sm text-gray-300">
                  How does this support public health?
                </p>
                <p className="text-sm mt-1">
                  Better understanding leads to safer usage, reduced adverse events,
                  and improved health literacy.
                </p>
              </div>

              <div>
                <p className="font-semibold text-green-400">🚀 Optimist</p>
                <p className="text-sm text-gray-300">
                  Can this become a national or global standard?
                </p>
                <p className="text-sm mt-1">
                  Yes. MediSense can act as a digital public infrastructure layer
                  for medicine safety and accessibility.
                </p>
              </div>
            </div>
          </section>
        )}



      </main>

      <footer className="bg-black border-t border-green-500 text-white py-4 px-4">
        <div className="flex flex-col lg:flex-row justify-between items-center text-center">
          <p className="text-xs lg:text-sm">&copy; 2026 Think5Whys Global DeepTech Solutions.</p>
          <div className="flex text-white space-x-6 mt-3 text-xs lg:text-sm">
            <Link to="/about-us" className="hover:text-green-300">About</Link>
            <Link to="/privacy-policy" className="hover:text-green-300">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-green-300">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-green-300">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
